import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { API_BASE_URL } from "@/lib/apiBase";
import { useAuth } from "@/scripts/AuthContext";
import { Button } from "@/components/ui/button";

/**
 * Bridge from apps.circa.company Install CTA → org Circatrons with pack wizard.
 */
export default function AppsInstallPage() {
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const packId = String(params.get("pack") || "")
    .trim()
    .toLowerCase();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [picking, setPicking] = useState(false);

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${accessToken}` }),
    [accessToken]
  );

  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API_BASE_URL}/org/memberships`, {
          headers,
        });
        const list = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.orgs)
            ? res.data.orgs
            : Array.isArray(res.data?.memberships)
              ? res.data.memberships
              : [];
        const normalized = list
          .map((row) => ({
            org_id: row.org_id || row.orgId || row.organization?.org_id,
            name:
              row.name ||
              row.org_name ||
              row.organization?.name ||
              row.org_id ||
              "Organization",
          }))
          .filter((o) => o.org_id);
        if (!cancelled) setOrgs(normalized);
      } catch (err) {
        if (!cancelled) {
          setError(
            err.response?.data?.message ||
              err.message ||
              "Could not load workplaces"
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken, headers]);

  useEffect(() => {
    if (loading || picking || error) return;
    if (orgs.length !== 1) return;
    const orgId = orgs[0].org_id;
    const q = new URLSearchParams({ section: "connectors" });
    if (packId) q.set("pack", packId);
    navigate(`/orgs/${encodeURIComponent(orgId)}/settings?${q.toString()}`, {
      replace: true,
    });
  }, [loading, picking, error, orgs, packId, navigate]);

  const goOrg = (orgId) => {
    setPicking(true);
    const q = new URLSearchParams({ section: "connectors" });
    if (packId) q.set("pack", packId);
    navigate(`/orgs/${encodeURIComponent(orgId)}/settings?${q.toString()}`);
  };

  if (!packId) {
    return (
      <div className="mx-auto max-w-lg px-4 py-16 space-y-4">
        <h1 className="text-xl font-semibold">Install Circatron</h1>
        <p className="text-sm text-muted-foreground">
          Missing pack id. Browse the marketplace and choose a Circatron to
          install.
        </p>
        <Button asChild variant="outline">
          <a href="https://apps.circa.company/">Open marketplace</a>
        </Button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-sm text-muted-foreground">
        Loading workplaces…
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-lg px-4 py-16 space-y-3">
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
        <Button asChild variant="outline">
          <Link to="/dashboard">Dashboard</Link>
        </Button>
      </div>
    );
  }

  if (orgs.length === 0) {
    return (
      <div className="mx-auto max-w-lg px-4 py-16 space-y-3">
        <h1 className="text-xl font-semibold">No workplace yet</h1>
        <p className="text-sm text-muted-foreground">
          Join or create a workplace, then install{" "}
          <span className="font-mono">{packId}</span> from Circatrons settings.
        </p>
        <Button asChild>
          <Link to="/dashboard">Go to dashboard</Link>
        </Button>
      </div>
    );
  }

  if (orgs.length === 1) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-sm text-muted-foreground">
        Opening Circatrons…
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg px-4 py-16 space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Choose a workplace</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Install <span className="font-mono">{packId}</span> into which
          workplace?
        </p>
      </div>
      <ul className="divide-y divide-border rounded-lg border border-border">
        {orgs.map((o) => (
          <li key={o.org_id}>
            <button
              type="button"
              className="flex w-full items-center justify-between px-3 py-3 text-left text-sm hover:bg-muted/50"
              onClick={() => goOrg(o.org_id)}
            >
              <span className="font-medium truncate">{o.name}</span>
              <span className="text-xs text-muted-foreground">Install</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
